import { Badge } from '../../components/Badge';
import { DiscountPriceDisplay } from '../../components/PriceDisplay';
import { PlusIcon } from '../../icons/PlusIcon';
import {
  ActionButton,
  BottomRow,
  CardBase,
  Meta,
  MiddleColumn,
  Price,
  SpotsLeft,
  Title,
  TopRow,
} from './SpaClassCompactCard.styles';
import type { SpaClassCompactCardProps } from './SpaClassCompactCard.types';

const defaultFormatSpotsLeft = (spots: number) => `${spots} ${spots === 1 ? 'spot' : 'spots'} left`;

function buildMeta(duration: string | undefined, instructor: string | undefined): string {
  const parts: string[] = [];
  if (duration) parts.push(duration);
  if (instructor) parts.push(instructor);
  return parts.join(' · ');
}

export function SpaClassCompactCard({
  id,
  name,
  time,
  price,
  discount,
  duration,
  instructor,
  spotsLeft,
  lowAvailabilityThreshold = 2,
  unavailable,
  soldOutLabel = 'Sold out',
  formatSpotsLeft = defaultFormatSpotsLeft,
  bookAriaLabel,
  onSelect,
}: SpaClassCompactCardProps) {
  const meta = buildMeta(duration, instructor);
  const showSpotsLeft = !unavailable && typeof spotsLeft === 'number' && spotsLeft > 0;
  const isLow = typeof spotsLeft === 'number' && spotsLeft <= lowAvailabilityThreshold;

  return (
    <CardBase data-testid="spa-class-card" $unavailable={unavailable}>
      <TopRow>
        <Badge tone="neutral">{time}</Badge>
        <MiddleColumn>
          <Title>{name}</Title>
          {meta && <Meta>{meta}</Meta>}
        </MiddleColumn>
        {!unavailable && price && (
          <Price>
            <DiscountPriceDisplay price={price} discount={discount} size="small" />
          </Price>
        )}
      </TopRow>

      <BottomRow>
        {unavailable ? (
          <Badge tone="neutral">{soldOutLabel}</Badge>
        ) : showSpotsLeft ? (
          <SpotsLeft $low={isLow}>{formatSpotsLeft(spotsLeft as number)}</SpotsLeft>
        ) : (
          <span />
        )}
        <ActionButton
          type="button"
          aria-label={bookAriaLabel ?? `${name} ${time}`}
          disabled={unavailable}
          onClick={() => onSelect(id)}
        >
          <PlusIcon />
        </ActionButton>
      </BottomRow>
    </CardBase>
  );
}
